<div class="space-y-6">
  <app-page-header
    title="Zones"
    subtitle="Découpage géographique de la plateforme. Les opérateurs sont rattachés à une zone par leur `zoneCode`."
  >
    <button
      slot="actions"
      type="button"
      (click)="openCreate()"
      class="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600"
    >
      Nouvelle zone
    </button>
  </app-page-header>

  @if (error() || loading() || items().length === 0) {
    <app-data-state
      [loading]="loading()"
      [error]="error()"
      [empty]="!loading() && !error() && items().length === 0"
      loadingMessage="Chargement des zones…"
      emptyMessage="Aucune zone configurée. Crée la première."
      (retry)="load()"
    />
  } @else {
    <section class="rounded-2xl border border-gray-200 bg-white shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead class="bg-gray-50 dark:bg-white/[0.02]">
            <tr>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Code</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Nom</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Type</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Polygone GeoJSON</th>
              <th class="px-5 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
            @for (z of items(); track z.code) {
              <tr class="transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                <td class="whitespace-nowrap px-5 py-4 font-mono text-xs text-gray-700 dark:text-gray-300">{{ z.code }}</td>
                <td class="whitespace-nowrap px-5 py-4 text-sm text-gray-800 dark:text-white/90">{{ z.name }}</td>
                <td class="whitespace-nowrap px-5 py-4">
                  <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {{ typeLabel[z.zoneType] }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-5 py-4">
                  @if (z.polygon) {
                    <span class="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">
                      ✓ défini
                    </span>
                  } @else {
                    <span class="text-xs text-gray-400">non défini</span>
                  }
                </td>
                <td class="whitespace-nowrap px-5 py-4 text-right">
                  <div class="inline-flex items-center gap-2">
                    <button type="button" (click)="openPolygon(z)" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
                      Polygone
                    </button>
                    <button type="button" (click)="openEdit(z)" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
                      Modifier
                    </button>
                    <button type="button" (click)="openDelete(z)" class="rounded-lg border border-error-300 px-3 py-1.5 text-xs font-medium text-error-700 transition hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10">
                      Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }
</div>

<!-- Create / Edit modal -->
<app-modal [isOpen]="editOpen()" (close)="closeEdit()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">
    @if (editMode() === 'create') { Nouvelle zone } @else { Modifier la zone }
  </h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    @if (editMode() === 'create') {
      Le <strong>code</strong> est immuable une fois créé. Il référence les opérateurs.
    } @else {
      Le code <strong class="font-mono">{{ editCode() }}</strong> ne peut pas être modifié.
    }
  </p>

  @if (editError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ editError() }}
    </div>
  }

  <form [formGroup]="editForm" (ngSubmit)="submitEdit()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Code *</label>
      <input
        type="text"
        formControlName="code"
        placeholder="DKR-PLATEAU"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 font-mono text-xs text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 read-only:bg-gray-50 disabled:bg-gray-50 disabled:opacity-70 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:read-only:bg-gray-800/60 dark:disabled:bg-gray-800/60"
      />
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nom *</label>
      <input
        type="text"
        formControlName="name"
        placeholder="Plateau (Dakar)"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Type *</label>
      <app-pretty-select formControlName="zoneType" [options]="typeOptions" />
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeEdit()" [disabled]="editSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="editSaving() || editForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (editSaving()) {
          @if (editMode() === 'create') { Création… } @else { Enregistrement… }
        } @else {
          @if (editMode() === 'create') { Créer } @else { Enregistrer }
        }
      </button>
    </div>
  </form>
</app-modal>

<!-- Polygon modal -->
<app-modal [isOpen]="polyOpen()" (close)="closePolygon()" className="max-w-2xl p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">
    Polygone GeoJSON — <span class="font-mono">{{ polyCode() }}</span>
  </h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    Colle un polygone GeoJSON valide. Sera utilisé pour la résolution opérateur par coordonnées GPS.
  </p>

  @if (polyError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ polyError() }}
    </div>
  }

  <form [formGroup]="polyForm" (ngSubmit)="submitPolygon()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">GeoJSON *</label>
      <textarea
        rows="10"
        formControlName="geoJsonPolygon"
        placeholder='{"type":"Polygon","coordinates":[[[-17.45,14.69],[-17.43,14.69],[-17.43,14.70],[-17.45,14.70],[-17.45,14.69]]]}'
        class="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 font-mono text-xs text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      ></textarea>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Le polygone est validé côté serveur avant enregistrement.</p>
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closePolygon()" [disabled]="polySaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="polySaving() || polyForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (polySaving()) { Enregistrement… } @else { Enregistrer }
      </button>
    </div>
  </form>
</app-modal>

<!-- Delete confirm -->
<app-confirm-modal
  [open]="deleteOpen()"
  [busy]="deleting()"
  [error]="deleteError()"
  title="Supprimer cette zone ?"
  confirmLabel="Supprimer définitivement"
  busyLabel="Suppression…"
  danger
  (confirm)="confirmDelete()"
  (close)="closeDelete()"
>
  La zone <strong class="font-mono">{{ deleteCode() }}</strong> sera retirée du référentiel.
  Les opérateurs liés perdront leur résolution par zone.
</app-confirm-modal>
